import type { FC } from 'react';
import { memo } from 'react';
import { FormattedMessage } from 'react-intl';

import { FacetBadges } from '../../../../../front/src/thread/Category/FacetBadges';
import { TopicSubject } from '../../../../../front/src/thread/Topic/TopicSubject';
import type { PressArticlesBlockData } from '../../../../../front/src/types/layout';
import {
  ArticleMeta,
  CategoryLinks,
  getHeroSrc,
  HeroImage,
  pressMessages,
  SectionHead,
  StretchedTitleLink,
} from './pressShared';

interface Props {
  block: PressArticlesBlockData;
  className?: string;
}

/**
 * "À la une" — magazine headline: one lead article (cover + overlapping panel)
 * plus a short list of secondary articles. Media mode only.
 */
export const BlockPressHeadline: FC<Props> = memo(function BlockPressHeadline({ block }) {
  const articles = block.data || [];
  if (!articles.length) return null;

  const [lead, ...rest] = articles;
  const secondary = rest.slice(0, 3);
  const heroSrc = getHeroSrc(lead);

  return (
    <section className="BlockPressHeadline max-w-5xl mx-auto px-7 pt-11">
      <SectionHead
        title={block.options?.customTitle || <FormattedMessage {...pressMessages.headline} />}
      />
      <div className="flex flex-wrap gap-10 items-start">
        {/* Lead */}
        <article className="relative group flex-1 basis-[540px] min-w-[340px]">
          <div className="relative aspect-[4/3] rounded-md overflow-hidden bg-gray-100">
            {heroSrc && (
              <HeroImage
                src={heroSrc}
                alt={lead.hero?.alt ?? ''}
                sizes="(max-width: 768px) 100vw, 540px"
                className="object-cover"
              />
            )}
          </div>
          <div className="relative w-[88%] -mt-[62px] bg-white rounded-md border-t-4 border-primary-base shadow-[0_16px_38px_rgba(20,20,24,0.14)] px-7 pt-5 pb-6">
            {/* Tag = lien distinct vers la rubrique, au-dessus du lien étiré (z-10). */}
            <CategoryLinks categories={lead.categories} className="relative z-10 mb-2.5" />
            <h3 className="!font-[family-name:var(--cv-display,'Patua_One',serif)] text-[clamp(25px,2.6vw,32px)] leading-[1.06] tracking-tight font-bold m-0 mb-3 group-hover:text-primary-base">
              <StretchedTitleLink slug={lead.slug}>
                <TopicSubject subject={lead.subject as string} />
              </StretchedTitleLink>
            </h3>
            <ArticleMeta
              author={lead.author}
              date={lead.createdAt}
              reactions={lead.messagesCount}
              className="text-sm"
            />
            {/* Facettes (B2_05, D22/D23) — cap 3 ; disableLink inchangé (comportement
                d'origine), relative pour rester au-dessus du lien étiré du titre. */}
            <FacetBadges
              themes={lead.themes}
              entities={lead.entities}
              max={3}
              disableLink
              plain
              className="relative z-10 flex flex-wrap items-center mt-2.5"
            />
          </div>
        </article>

        {/* Secondary */}
        {!!secondary.length && (
          <div className="flex-1 basis-[320px] min-w-[296px] flex flex-col border-t-2 border-gray-darkv4">
            {secondary.map(article => (
              <div
                key={article.id}
                className="relative group flex flex-col py-5 border-b border-black/10"
              >
                {/* Rangée texte + vignette */}
                <div className="flex gap-[18px] items-start">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-[19px] leading-[1.22] font-bold mb-1.5 group-hover:text-primary-base">
                      <StretchedTitleLink slug={article.slug}>
                        <TopicSubject subject={article.subject as string} />
                      </StretchedTitleLink>
                    </h4>
                    <ArticleMeta
                      author={article.author}
                      date={article.createdAt}
                      reactions={article.messagesCount}
                    />
                  </div>
                  {getHeroSrc(article) && (
                    <HeroImage
                      src={getHeroSrc(article) as string}
                      width={92}
                      height={92}
                      sizes="92px"
                      className="flex-none w-[92px] h-[92px] rounded object-cover bg-gray-100"
                    />
                  )}
                </div>
                {/* Tags EN PLEINE LARGEUR sous la rangée texte+image : scroll + fondu à droite. */}
                <FacetBadges
                  themes={article.themes}
                  entities={article.entities}
                  disableLink
                  plain
                  className="relative z-10 mt-1.5 flex w-full min-w-0 flex-nowrap items-center overflow-x-auto whitespace-nowrap [-ms-overflow-style:none] [scrollbar-width:none] [mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)] [&::-webkit-scrollbar]:hidden"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
});
